import classnames from 'classnames/bind'
import { useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'

import RichText, { RichTextBlock } from '~/components/Abstracts/RichText'
import EditorialSliderImagesWithCaptions from '~/components/Slices/EditorialSliderImagesWithCaptions'
// import { useNavBannerContext } from '~/components/Navigation/NavBanner'
import ReviewForm from '~/components/UI/ReviewPageContent/ReviewForm'
import ReviewHeader from '~/components/UI/ReviewPageContent/ReviewHeader'

import { useStyle } from '~/providers/StyleProvider'

import { REVIEWS } from '~/data/dictionary'
import { ReviewPageData } from '~/data/review-page-data/serializer'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ReviewPageContentProps extends ReviewPageData {
  className?: string
}

export interface ReviewPageHeaderProps {
  className?: string
  title?: string
  text?: RichTextBlock
}

function ReviewPageHeader({ className, title, text }: ReviewPageHeaderProps) {
  const t = useTranslate()
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title26_36Primary,
  })
  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text18Secondary,
  })

  return (
    <div className={cx(css.ReviewPageHeader, className)}>
      <h1 className={cx(css.title, titleStyle)}>
        {title ? title : t(REVIEWS.TITLE)}
      </h1>
      <RichText className={cx(css.paragraph, textStyle)} render={text} />
    </div>
  )
}

function ReviewPageContent({
  className,
  id,
  productInfos,
  pageHeader,
  slider,
}: ReviewPageContentProps) {
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const [hasReviewed, setHasReviewed] = useState(false)
  const borderStyle = useStyle({ borderColor: GlobalThemeColors.Gray3ToBlack })
  const { title, text, thankyouText } = pageHeader ?? {}

  const onReviewSent = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    setHasReviewed(true)
  }

  return (
    <div
      className={cx(css.ReviewPageContent, className, { withBanner: false })}>
      <div className={cx(css.container, gridStyle, borderStyle)}>
        {(title || text) && (
          <ReviewPageHeader
            className={cx(css.hideOnLargeScreen, css.pageHeader)}
            title={title}
            text={text}
          />
        )}
        {productInfos && (
          <ReviewHeader className={cx(css.header)} {...productInfos} />
        )}
        <div className={cx(css.formContainer)}>
          {(title || text) && (
            <ReviewPageHeader
              className={cx(css.hideOnSmallScreen)}
              title={title}
              text={hasReviewed ? thankyouText : text}
            />
          )}
          {id && (
            <ReviewForm
              className={cx(css.form)}
              productId={id}
              productLink={productInfos?.link}
              hasReviewed={hasReviewed}
              onReviewSent={onReviewSent}
            />
          )}
        </div>
      </div>
      {slider && (
        <EditorialSliderImagesWithCaptions
          className={cx(css.slider)}
          {...slider}
        />
      )}
    </div>
  )
}

ReviewPageContent.defaultProps = {}

export default ReviewPageContent
